import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Play, Square, Trophy, User, Award } from 'lucide-react';
import { formatPersianDate } from '../../utils/dateUtils';
import { getFullImageUrl } from '../../utils/imageUpload';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import matchService from '../../services/matchService';
import MatchModal from '../../components/Modals/MatchModal';
import MatchResultModal from '../../components/Tournament/MatchResultModal';
import toast from 'react-hot-toast';

const MatchesContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    gap: 10px;
    width: 100%;
    & > * {
      flex: 1 1 100%;
    }
  }
`;

const MatchesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const MatchCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => {
      switch(props.status) {
        case 'active': return 'linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)';
        case 'completed': return 'linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)';
        case 'pending': return 'linear-gradient(90deg, #ffd93d 0%, #ff6b6b 100%)';
        default: return 'linear-gradient(90deg, #6c6c8a 0%, #4a4a6a 100%)';
      }
    }};
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
  @media (max-width: 768px) {
    border-radius: 10px;
  }
`;

const MatchContent = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 20px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 14px;
    gap: 14px;
  }
`;

const MatchImageWrapper = styled.div`
  width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #0f1535;
  border: 1px solid #2d2d44;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  @media (max-width: 768px) {
    width: 100%;
    height: 160px;
    border-radius: 8px;
  }
`;

const Select = styled.select`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 10px 12px;
  color: #ffffff;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
  option {
    background: #1a1a2e;
    color: #ffffff;
  }
`;

const MatchInfo = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 20px;
  align-items: center;
  @media (max-width: 1024px) {
    grid-template-columns: 2fr 1fr 1fr;
  }
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const MatchTitle = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const MatchId = styled.div`
  color: #6c6c8a;
  font-size: 12px;
`;

const MatchStatus = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'active': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'completed': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      case 'pending': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
  margin-top: 5px;
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 4px 10px;
  }
`;

const MatchDetails = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  @media (max-width: 768px) {
    font-size: 13px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
  }
`;

const MatchDetailItem = styled.div`
  margin-bottom: 5px;
`;

const MatchDetailLabel = styled.span`
  color: #6c6c8a;
  margin-left: 5px;
`;

const MatchActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    justify-content: space-between;
    gap: 8px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
  @media (max-width: 768px) {
    flex: 1 1 calc(50% - 8px);
    min-width: 44px;
    padding: 10px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const Matches = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isActiveFilter, setIsActiveFilter] = useState('all');
  const [isClosedFilter, setIsClosedFilter] = useState('all');

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const response = await matchService.getMatches();
      setMatches(response || []);
    } catch (error) {
      toast.error('خطا در بارگذاری مسابقات');
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = matches.filter(match => {
    const term = searchTerm.toLowerCase();
    const matchesTerm = (
      match.name?.toLowerCase().includes(term) ||
      match.description?.toLowerCase().includes(term)
    );
    const matchesActive = (
      isActiveFilter === 'all' ||
      (isActiveFilter === 'true' && Boolean(match.isActive)) ||
      (isActiveFilter === 'false' && !Boolean(match.isActive))
    );
    const matchesClosed = (
      isClosedFilter === 'all' ||
      (isClosedFilter === 'true' && Boolean(match.isClosed)) ||
      (isClosedFilter === 'false' && !Boolean(match.isClosed))
    );
    return matchesTerm && matchesActive && matchesClosed;
  });

  const handleAddMatch = () => {
    setSelectedMatch(null);
    setShowMatchModal(true);
  };

  const handleEditMatch = (match) => {
    setSelectedMatch(match);
    setShowMatchModal(true);
  };

  const handleAddResult = (match) => {
    setSelectedMatch(match);
    setShowResultModal(true);
  };

  const handleShowPlayers = (match) => {
    // TODO: Implement show players modal
    toast.info('قابلیت مشاهده بازیکنان در حال توسعه است');
  };

  const handleStartMatch = async (matchId) => {
    try {
      await matchService.startMatch(matchId);
      toast.success('مسابقه با موفقیت شروع شد');
      fetchMatches();
    } catch (error) {
      toast.error('خطا در شروع مسابقه');
      console.error('Error starting match:', error);
    }
  };

  const handleEndMatch = async (matchId) => {
    try {
      await matchService.endMatch(matchId);
      toast.success('مسابقه با موفقیت به پایان رسید');
      fetchMatches();
    } catch (error) {
      toast.error('خطا در پایان مسابقه');
      console.error('Error ending match:', error);
    }
  };

  const handleModalClose = () => {
    setShowMatchModal(false);
    setSelectedMatch(null);
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    setSelectedMatch(null);
  };

  const handleModalSuccess = () => {
    fetchMatches();
  };

  const formatDate = (dateString) => {
    return formatPersianDate(dateString, "YYYY/MM/DD HH:mm:ss");
  };

  const getGameTypeName = (type) => {
    return type === 0 ? 'Battle Royale' : 'Team Dead Match';
  };

  const getMatchStatus = (match) => {
    if (!match.isActive && match.isClosed) {
      return 'completed';
    }
    if (match.isActive) {
      return 'active';
    }
    return 'pending';
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'active': return 'در حال انجام';
      case 'completed': return 'تکمیل شده';
      case 'pending': return 'در انتظار';
      default: return 'نامشخص';
    }
  };

  if (loading) {
    return (
      <MatchesContainer>
        <LoadingSpinner>در حال بارگذاری مسابقات...</LoadingSpinner>
      </MatchesContainer>
    );
  }

  return (
    <MatchesContainer>
      <Header>
        <Title>مدیریت مسابقات</Title>
        <SearchContainer>
          <Input
            placeholder="جستجو در مسابقات..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Select
            value={isActiveFilter}
            onChange={(e) => setIsActiveFilter(e.target.value)}
            style={{ minWidth: '150px' }}
          >
            <option value="all">همه وضعیت فعال</option>
            <option value="true">فعال</option>
            <option value="false">غیرفعال</option>
          </Select>
          <Select
            value={isClosedFilter}
            onChange={(e) => setIsClosedFilter(e.target.value)}
            style={{ minWidth: '150px' }}
          >
            <option value="all">همه وضعیت بسته</option>
            <option value="true">بسته</option>
            <option value="false">باز</option>
          </Select>
          <Button variant="primary" onClick={handleAddMatch} icon={<Plus size={16} />}>
            افزودن مسابقه
          </Button>
        </SearchContainer>
      </Header>

      {filteredMatches.length === 0 ? (
        <EmptyState>
          <EmptyIcon>⚔️</EmptyIcon>
          <EmptyTitle>هیچ مسابقه‌ای یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز مسابقه‌ای ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <MatchesList>
          {filteredMatches.map((match, index) => {
            const status = getMatchStatus(match);
            return (
              <MatchCard
                key={match.id || index}
                status={status}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <MatchContent>
                  {match.matchPicture && (
                    <MatchImageWrapper>
                      <img src={getFullImageUrl(match.matchPicture)} alt={match.name || 'match'} />
                    </MatchImageWrapper>
                  )}
                  <MatchInfo>
                    <div>
                      <MatchTitle>{match.name || 'مسابقه بدون نام'}</MatchTitle>
                      <MatchId>#{match.id}</MatchId>
                      <MatchStatus status={status}>
                        {getStatusText(status)}
                      </MatchStatus>
                    </div>
                    
                    <MatchDetails>
                      <MatchDetailItem>
                        نوع بازی: {getGameTypeName(match.gameType)}
                      </MatchDetailItem>
                      <MatchDetailItem>
                        <MatchDetailLabel>ورودی:</MatchDetailLabel>
                        {match.entryScore} امتیاز (${match.entryCost})
                      </MatchDetailItem>
                    </MatchDetails>
                    
                    <MatchDetails>
                      <MatchDetailItem>
                        <MatchDetailLabel>شروع:</MatchDetailLabel>
                        {new Date(match.startDate).toLocaleDateString('fa-IR')}
                      </MatchDetailItem>
                      <MatchDetailItem>
                        <MatchDetailLabel>مهلت:</MatchDetailLabel>
                        {new Date(match.reservationDeadline).toLocaleDateString('fa-IR')}
                      </MatchDetailItem>
                    </MatchDetails>
                  </MatchInfo>
                  
                  <MatchActions>
                    {!match.isClosed && !match.isActive && (
                      <ActionButton onClick={() => handleStartMatch(match.id)} title="شروع مسابقه">
                        <Play size={16} />
                      </ActionButton>
                    )}
                    {match.isActive && !match.isClosed && (
                      <ActionButton onClick={() => handleEndMatch(match.id)} title="پایان مسابقه">
                        <Square size={16} />
                      </ActionButton>
                    )}
                    <ActionButton onClick={() => handleEditMatch(match)} title="ویرایش">
                      <Edit size={16} />
                    </ActionButton>
                    <ActionButton onClick={() => handleAddResult(match)} title="افزودن نتیجه">
                      <Trophy size={16} />
                    </ActionButton>
                    <ActionButton onClick={() => handleShowPlayers(match)} title="مشاهده بازیکنان">
                      <User size={16} />
                    </ActionButton>
                  </MatchActions>
                </MatchContent>
              </MatchCard>
            );
          })}
        </MatchesList>
      )}

      <MatchModal
        show={showMatchModal}
        match={selectedMatch}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />

      <MatchResultModal
        show={showResultModal}
        match={selectedMatch}
        onClose={handleResultModalClose}
        onSuccess={handleModalSuccess}
      />
    </MatchesContainer>
  );
};

export default Matches;